import React, {useLayoutEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types';
import utils from 'helpers/utils';
import {useAssistantState, useCardState, useEffectEvent, useGraphState} from 'helpers/hooks/utils';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';

import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import {useTour} from 'components/organisms/Providers/TourProvider/TourProvider';
import constants from 'helpers/constants';
import useMediaQuery from '@mui/material/useMediaQuery';

export const ProfileContext = React.createContext(null);

export function useProfile () {
  return React.useContext(ProfileContext);
}

const ProfileProvider = (props) => {
  const profileRef = useRef(null);

  const [internalState, setInternalState] = useState({
    activeView: null,
    isEditing: false,
    isSubmitting: false,
    // see also clearState
    showSidebar: {
      profile: true,
      browser: false
    },
    showContext: {
      profile: true,
      browser: true
    },
    showProfile: false,
    showTask: false,
    showToc: false,
    settings: {}
  });

  const viewInitialised = useRef(null);
  const definitionsInitialised = useRef(false);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // see also TableProvider
  const optionsMemo = useMemo(() => {
    const options = utils.mergeObjects(props?.options ?? {}, props?.view?.options, true);
    Object.keys(options).forEach((k) => {
      if (options[k]?.options && options[k].options.type) {
        if (props?.view || props.skipStorage) {
          if (props?.view && (props?.view?.id !== 'default') && options[k].options.name) {
            options[k].options.name = `${options[k].options.name}_${props.view.id}`;
          }
          if (props?.view?.skipStorage || props.skipStorage) {
            if (props.skipStorage) {
              options[k].options.searchParams = false;
            }
            options[k].options.type = constants.appState.type.none;
          }
        }
      }
    });

    if (!props?.isDefinitionsLoading || definitionsInitialised.current) {
      definitionsInitialised.current = true;

      const cardVisibility = (options?.cardDefinitions || []).reduce((o, cardDef) => {
        if (utils.isDefined(cardDef.visible)) {
          o = o ?? {};
          o[cardDef.name] = cardDef.visible;
        }
        return o;
      }, options?.cardState?.initial?.cardVisibility);

      const graphVisibility = (options?.graphDefinitions || []).reduce((o, graphDef) => {
        if (utils.isDefined(graphDef.visible)) {
          o = o ?? {};
          o[graphDef.name] = graphDef.visible;
        }
        return o;
      }, options?.graphState?.initial?.graphVisibility);

      return {
        ...options,
        cardState: {
          ...options?.cardState,
          initial: {
            ...options?.cardState?.initial,
            cardVisibility: cardVisibility
          }
        },
        graphState: {
          ...options?.graphState,
          initial: {
            ...options?.graphState?.initial,
            graphVisibility: graphVisibility
          }
        }
      };
    } else {
      return {};
    }
  }, [props?.options, props?.skipStorage, props?.view, props?.isDefinitionsLoading]);

  const cardState = useCardState(optionsMemo?.cardState?.initial, optionsMemo?.cardState?.options);
  const graphState = useGraphState(optionsMemo?.graphState?.initial, optionsMemo?.graphState?.options);
  const assistantState = useAssistantState(optionsMemo?.assistantState?.initial, optionsMemo?.assistantState?.options);

  const tableProvider = useTable();
  const authorize = useAuthorize();
  const tour = useTour();

  const cardDefinitions = useMemo(() => {
    const cardDefs = props.options?.cardDefinitions;
    if (cardDefs?.length > 0) {
      return cardDefs
        .filter((cardDef) => !cardDef.hidden && authorize(cardDef.auth?.read ?? {}))
        .map((cardDef) => {
          cardDef.fields = (cardDef.fields ?? [])
            .filter((f) => !f.hidden && authorize(f.auth?.read ?? {}));
          return cardDef;
        });
    } else {
      return null;
    }
  }, [authorize, props.options?.cardDefinitions]);

  const sectionDefinitions = useMemo(() => {
    const sectionDefs = props.options?.sectionDefinitions;
    if (sectionDefs?.length > 0) {
      return sectionDefs
        .filter((sectionDef) => !sectionDef.hidden && authorize(sectionDef.auth?.read ?? {}))
        .map((sectionDef) => {
          sectionDef.cards = !sectionDef.cards ? null : sectionDef.cards
            .filter((c) => !c.hidden && authorize(c.auth?.read ?? {}));
          return sectionDef;
        });
    } else {
      return null;
    }
  }, [authorize, props.options?.sectionDefinitions]);

  const dataRef = useRef(null);
  const dataMemo = useMemo(() => {
    if (utils.isDefined(props.data?.data) || props.data?.status?.hasError) {
      // keep the last available data
      dataRef.current = props.data;
    }
    return dataRef.current;
  }, [props.data]);

  const viewId = utils.isDefined(props.view?.id) ? (props.view?.id + (smDown ? '_smDown' : '')) : 'default';
  const initialised = internalState.activeView === viewId;
  const tableIsLoading = Boolean(tableProvider?.isLoading());
  const context = useMemo(() => {
    return {
      state: {
        ...internalState,
        ...props.state
      },
      refs: {
        profileRef
      },
      cardState,
      cardDefinitions,
      sectionDefinitions,
      graphState,
      assistantState,
      initialised,
      dataKey: props.dataKey,
      data: dataMemo,
      context: props.context,
      view: props.view,
      fieldData: props.fieldData,
      updaters: props.updaters,
      clearState: () => {
        cardState?.clear?.();
        setInternalState(utils.updater({
          // see also viewInit
          showSidebar: {
            profile: true,
            browser: false
          },
          showContext: {
            profile: true,
            browser: true
          },
          showProfile: false,
          showTask: false,
          showToc: false,
          settings: {}
        }, true));
      },
      edit: () => {
        setInternalState(utils.updater({
          isEditing: true,
          isSubmitting: false,
          validation: null,
          submitSuccess: null,
          submitError: null,
          showToc: true
        }, true)); // remove state
      },
      dirty: (isDirty) => {
        setInternalState(utils.updater({
          isDirty
        }, true));
      },
      validation: (validation) => {
        setInternalState(utils.updater({
          validation: validation
        }, true));
      },
      error: (error) => {
        setInternalState(utils.updater({
          isSubmitting: false,
          validation: null,
          submitSuccess: null,
          submitError: error
        }, true));
      },
      success: (success) => {
        setInternalState(utils.updater({
          isSubmitting: false,
          isEditing: false,
          validation: null,
          submitError: null,
          submitSuccess: success,
          showToc: false
        }, true));
      },
      submit: () => {
        setInternalState(utils.updater({
          isSubmitting: true,
          validation: null,
          submitError: null,
          submitSuccess: null
        }, true));
        profileRef.current?.submit?.();
      },
      cancel: () => {
        setInternalState(utils.updater({
          isDirty: false,
          isEditing: false,
          isSubmitting: false,
          showToc: false
        }, true));
        profileRef.current?.reset?.();
      },
      isLoading: () => {
        return Boolean(tableIsLoading || props.isLoading || !dataMemo?.data);
      },
      toggleSidebar: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showSidebar: {
              ...current.showSidebar,
              [key]: !current.showSidebar[key]
            }
          })(current);
        });
      },
      openSidebar: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showSidebar: {
              ...current.showSidebar,
              [key]: true
            }
          })(current);
        });
      },
      closeSidebar: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showSidebar: {
              ...current.showSidebar,
              [key]: false
            }
          })(current);
        });
      },
      toggleContext: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showContext: {
              ...current.showContext,
              [key]: !current.showContext[key]
            }
          })(current);
        });
      },
      openContext: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showContext: {
              ...current.showContext,
              [key]: true
            }
          })(current);
        });
      },
      closeContext: (key) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showContext: {
              ...current.showContext,
              [key]: false
            }
          })(current);
        });
      },
      toggleProfile: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showProfile: !current.showProfile
          })(current);
        });
      },
      openProfile: (settings) => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showProfile: true,
            settings: {
              ...current.settings,
              ...settings
            }
          })(current);
        });
      },
      closeProfile: () => {
        setInternalState(utils.updater({showProfile: false}, true));
      },
      toggleTask: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showTask: !current.showTask
          })(current);
        });
      },
      openTask: (id) => {
        setInternalState(utils.updater({showTask: id}, true));
      },
      closeTask: () => {
        setInternalState(utils.updater({showTask: false}, true));
      },
      toggleToc: () => {
        setInternalState((current) => {
          return utils.updater({
            ...current,
            showToc: !current.showToc
          })(current);
        });
      },
      openToc: () => {
        setInternalState(utils.updater({showToc: true}, true));
      },
      closeToc: () => {
        setInternalState(utils.updater({showToc: false}, true));
      },
      setSettings: (settings) => {
        if (!utils.isFunction(settings)) {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              settings: {
                ...current.settings,
                ...settings
              }
            })(current);
          });
        } else {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              settings: settings(current.settings)
            })(current);
          });
        }
      },
      setProfile: (profile) => {
        profileRef.current = profile;
        setInternalState(utils.updater({profileConnected: utils.isDefined(profile)}, true));
      }
    }
  }, [internalState, cardState, cardDefinitions, sectionDefinitions, graphState, assistantState,
    props.dataKey, props.view, props.updaters, props.fieldData, dataMemo, props.context, props.state,
    tableIsLoading, initialised, props.isLoading]);

  const reportEvent = useEffectEvent(tour?.report);
  useLayoutEffect(() => {
    if (props.view?.type) {
      reportEvent?.(`profile:${props.view.type}`, props.data);

      return () => {
        reportEvent?.(`profile:${props.view.type}`, null);
      };
    }
  }, [reportEvent, props.view?.type, props.data]);

  useLayoutEffect(() => {
    if (viewInitialised.current !== viewId) {
      const init = viewInitialised.current !== viewId;

      viewInitialised.current = viewId;
      setInternalState(utils.updater({
        // see also clearState
        activeView: viewId,
        ...(init ? {
          showSidebar: {
            profile: true,
            browser: false
          },
          showContext: {
            profile: true,
            browser: true
          },
          showProfile: false,
          showTask: false,
          showToc: false,
          settings: {}
        } : null)
      }, true));
    }
  }, [viewId]);

  return <ProfileContext.Provider value={context}>
    {props.children}
  </ProfileContext.Provider>
};

ProfileProvider.propTypes = {
  view: PropTypes.object,
  dataId: PropTypes.string,
  dataKey: PropTypes.string,
  data: PropTypes.object,
  context: PropTypes.object,
  options: PropTypes.object,
  fieldData: PropTypes.object,
  state: PropTypes.object,
  updaters: PropTypes.object,
  isLoading: PropTypes.bool,
  isDefinitionsLoading: PropTypes.bool
}

export default ProfileProvider;
